import * as z from 'zod';
import { ReservationItemResource } from './ReservationItem';
import { isoDateToAppDate, isoDateTimeToAppTime } from '../base-codecs.ts';
import { EntityId, Quantity, Tag, Ören } from '../base-schemas.ts';
import { EnrichedCustomer } from '../Customer.ts';
import { ValidateCartResponse } from './ValidateCart';
import { ReservationSources } from './ReservationSource';
import { ReservationItemPriceMode } from './ReservationItemPriceMode.ts';
export { ReservationSources } from './ReservationSource';
export const ReservationPaymentStatuses = z.enum(['partially-paid', 'fully-paid', 'unpaid', 'no-payment-required']);
const ReservationCustomerResource = z.looseObject({
    id: EntityId,
    name: z.string().nullable(),
    full_name: z.string().nullable(),
    email: z.string().nullable(),
    phone_number: z.string().nullable()
});
/**
 * Minimal booking shape as embedded in a reservation. Reservations only ever
 * read booking_number and remarks off this relation, so it's kept independent
 * of the general Booking model rather than importing it (see Booking.ts).
 */
const ReservationBookingResource = z.object({
    id: EntityId,
    booking_number: z.string(),
    position_id: EntityId.nullable(),
    check_in: isoDateToAppDate.nullable(),
    check_out: isoDateToAppDate.nullable(),
    remarks: z.string().nullable()
});
export const ReservationModel = z.object({
    _tag: Tag('ReservationModel'),
    id: EntityId,
    customer_id: EntityId.nullish(),
    booking_id: EntityId.nullish(),
    source: ReservationSources,
    customer: ReservationCustomerResource.nullish(),
    items: z.array(ReservationItemResource).nullish(),
    total_amount: Ören.nullable(),
    created_at: isoDateTimeToAppTime.nullable(),
    updated_at: isoDateTimeToAppTime.nullable(),
    deleted_at: isoDateTimeToAppTime.nullable(),
    confirmed_at: isoDateTimeToAppTime.nullish(),
    cancelled_at: isoDateTimeToAppTime.nullish(),
    // Attributes
    payment_status: ReservationPaymentStatuses,
    // Relations
    booking: ReservationBookingResource.nullish()
});
export const EnrichedReservation = ReservationModel.extend({
    _tag: Tag('EnrichedReservation'),
    isEnriched: z.literal(true),
    get customer() { return EnrichedCustomer.nullish(); }
});
export const CreateReservationInput = z.object({
    customer_id: EntityId.nullish(),
    booking_id: EntityId.nullish(),
    items: z.array(z.object({
        reservable_product_id: EntityId,
        preferred_reservable_ids: z.array(EntityId).nullish(),
        quantity: Quantity.optional(), // Integer format (1000 = 1 unit), defaults to 1000
        price: Ören.optional(),
        price_mode: ReservationItemPriceMode.optional(),
        reservation_option_id: EntityId.nullish(),
        is_manually_scheduled: z.boolean(),
        selection: z.unknown().optional(),
        note: z.string().nullish(),
        delivery_date_time: isoDateTimeToAppTime.optional(),
        return_date_time: isoDateTimeToAppTime.optional()
    })).min(1),
    send_confirmation: z.boolean().optional()
});
export const ReservationResource = z.object({
    id: EntityId,
    // Campingens initialer + år + R-id, t.ex. AB26-R123. Samma format som bokningsnummer.
    reservation_number: z.string().nullish(),
    customer_id: EntityId.nullable(),
    booking_id: EntityId.nullable(),
    source: ReservationSources,
    customer: ReservationCustomerResource.nullable(),
    booking: ReservationBookingResource.nullish(),
    items: z.array(ReservationItemResource),
    note: z.string().nullish(),
    total_amount: Ören.nullable(),
    payment_status: ReservationPaymentStatuses,
    created_at: isoDateTimeToAppTime.nullable(),
    updated_at: isoDateTimeToAppTime.nullable(),
    deleted_at: isoDateTimeToAppTime.nullable(),
    confirmed_at: isoDateTimeToAppTime.nullish(),
    cancelled_at: isoDateTimeToAppTime.nullish()
});
const CreatedReservationResponse = z.object({
    reservation_items: z.array(ReservationItemResource),
    booking: ReservationBookingResource.nullish(),
    get reservation() { return ReservationResource.nullish(); }
});
export const CreateReservationSuccessResponse = z.union([
    CreatedReservationResponse,
    ValidateCartResponse
]);
export const CreateReservationResponse = CreateReservationSuccessResponse;
export const ValidateReservationResponse = z.object({
    valid: z.boolean(),
    errors: z.record(z.string(), z.array(z.string())),
    items: z.array(z.object({
        valid: z.boolean(),
        errors: z.array(z.string()),
        warnings: z.array(z.string()),
        calculated_price: Ören.nullable(),
        available_reservables: z.array(EntityId)
    }))
});
